import './styles.scss'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { InputText } from 'primereact/inputtext'
import DialogUIModal from 'Components/UIComponent/DialogUIModal'
import Button from 'Components/UIComponent/Button'

const ForwardMessageModal = (props) => {
  const { forwardModalVisible, setForwardModalVisible, setMessageToForward, personalChats, selectedUserId, internalSelectedUserId, onForward, isForwarding, communities, onForwardToCommunities, isAdminUser } = props
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState('users')
  const [forwardSearchTerm, setForwardSearchTerm] = useState('')
  const [selectedUsersForForward, setSelectedUsersForForward] = useState([])
  const [filteredUsersForForward, setFilteredUsersForForward] = useState([])
  const [selectedCommunitiesForForward, setSelectedCommunitiesForForward] = useState([])
  const [filteredCommunitiesForForward, setFilteredCommunitiesForForward] = useState([])

  useEffect(() => {
    if (!forwardModalVisible) return

    const currentUserId = selectedUserId || internalSelectedUserId
    const availableUsers = personalChats.filter((chat) => {
      const userId = chat.other_user_id || chat.user_id || chat.id

      const isBlocked = chat.is_blocked_by_other || chat.is_blocked_by_me

      return String(userId) !== String(currentUserId) && !isBlocked
    })

    if (!forwardSearchTerm.trim()) {
      setFilteredUsersForForward(availableUsers)
    } else {
      const filtered = availableUsers.filter((chat) => {
        const name = (chat.other_user_name || `${chat.first_name || ''} ${chat.last_name || ''}`.trim() || '').toLowerCase()

        return name.includes(forwardSearchTerm.toLowerCase())
      })

      setFilteredUsersForForward(filtered)
    }
  }, [forwardSearchTerm, personalChats, forwardModalVisible, selectedUserId, internalSelectedUserId])

  useEffect(() => {
    if (!forwardModalVisible) {
      setForwardSearchTerm('')
      setSelectedUsersForForward([])
      setSelectedCommunitiesForForward([])
      setActiveTab('users')
    }
  }, [forwardModalVisible])

  // Filter communities based on search term
  useEffect(() => {
    if (!forwardModalVisible) return

    if (activeTab === 'communities') {
      const availableCommunities = (communities || []).filter((community) => !community.isCreateOwn)

      if (!forwardSearchTerm.trim()) {
        setFilteredCommunitiesForForward(availableCommunities)
      } else {
        const filtered = availableCommunities.filter((community) => {
          const name = (community.name || '').toLowerCase()

          return name.includes(forwardSearchTerm.toLowerCase())
        })

        setFilteredCommunitiesForForward(filtered)
      }
    }
  }, [forwardSearchTerm, communities, forwardModalVisible, activeTab])

  const handleUserCheckboxChange = (userId) => {
    setSelectedUsersForForward((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId)
      }

      return [...prev, userId]
    })
  }

  const handleCloseForwardModal = () => {
    setForwardModalVisible(false)
    setMessageToForward(null)
    setSelectedUsersForForward([])
    setSelectedCommunitiesForForward([])
    setForwardSearchTerm('')
    setActiveTab('users')
  }

  const handleCommunityCheckboxChange = (communityId) => {
    const community = (communities || []).find((item) => item.id === communityId)

    if (community?.isCreateOwn) return

    const isDisabled = community?.admin_only_message && !isAdminUser

    if (isDisabled) return

    setSelectedCommunitiesForForward((prev) => {
      if (prev.includes(communityId)) {
        return prev.filter((id) => id !== communityId)
      }

      return [...prev, communityId]
    })
  }

  const handleSelectAllCommunities = () => {
    const availableCommunities = (communities || []).filter((c) => !c.isCreateOwn && !(c.admin_only_message && !isAdminUser))
    const eligibleIds = filteredCommunitiesForForward
      .filter((c) => !c.isCreateOwn && !(c.admin_only_message && !isAdminUser))
      .map((c) => c.id)
      .filter(Boolean)
    const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedCommunitiesForForward.includes(id))

    if (allSelected) {
      setSelectedCommunitiesForForward((prev) => prev.filter((id) => !eligibleIds.includes(id)))
    } else {
      setSelectedCommunitiesForForward((prev) => {
        const newSelection = [...prev]

        eligibleIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id)
          }
        })

        return newSelection
      })
    }
  }

  const handleSelectAll = () => {
    const eligibleIds = filteredUsersForForward.map((user) => user.other_user_id || user.user_id || user.id).filter(Boolean)
    const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedUsersForForward.includes(id))

    if (allSelected) {
      setSelectedUsersForForward((prev) => prev.filter((id) => !eligibleIds.includes(id)))
    } else {
      setSelectedUsersForForward((prev) => {
        const newSelection = [...prev]

        eligibleIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id)
          }
        })

        return newSelection
      })
    }
  }

  const handleForward = () => {
    if (activeTab === 'users') {
      const selectedUsers = filteredUsersForForward.filter((user) => {
        const userId = user.other_user_id || user.user_id || user.id

        return selectedUsersForForward.includes(userId)
      })

      if (onForward && selectedUsers.length > 0) {
        onForward(selectedUsers)
      }
    } else if (activeTab === 'communities') {
      const selectedCommunities = filteredCommunitiesForForward.filter((community) => selectedCommunitiesForForward.includes(community.id))

      if (onForwardToCommunities && selectedCommunities.length > 0) {
        onForwardToCommunities(selectedCommunities)
      }
    }
  }

  const renderUsersTab = () => {
    const eligibleIds = filteredUsersForForward.map((user) => user.other_user_id || user.user_id || user.id).filter(Boolean)
    const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedUsersForForward.includes(id))

    return (
      <>
        <div className="search-section">
          <InputText placeholder="Search users" value={forwardSearchTerm} onChange={(e) => setForwardSearchTerm(e?.target?.value)} className="input-field" />
        </div>

        <div className="selected-info">
          <div className="selected-count">
            {t('SELECTED')} {selectedUsersForForward?.length} user{selectedUsersForForward?.length !== 1 ? 's' : ''}
          </div>
          <div className="select-all-container">
            {t('SELECT_ALL')} <input type="checkbox" checked={allSelected} onChange={handleSelectAll} className="select-all-checkbox" disabled={filteredUsersForForward?.length === 0} />
          </div>
        </div>

        <div className="forward-list">
          {filteredUsersForForward?.length === 0 ? (
            <div className="no-results">{t('NO_USERS_FOUND')}</div>
          ) : (
            filteredUsersForForward?.map((user) => {
              const userId = user.other_user_id || user.user_id || user.id
              const userName = user.other_user_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User'
              const avatarUrl = user.other_user_avatar_url || user.users_flag_url || user.avatar

              return (
                <div key={userId} className={`forward-list-item ${selectedUsersForForward?.includes(userId) ? 'selected' : ''}`} onClick={() => handleUserCheckboxChange(userId)}>
                  <div className="community-info">
                    {avatarUrl ? <img src={avatarUrl} alt={userName} className="community-avatar" /> : <div className="community-placeholder">{userName[0] || 'U'}</div>}
                    <div className="community-name">
                      <div>{userName}</div>
                    </div>
                  </div>
                  <input type="checkbox" checked={selectedUsersForForward?.includes(userId)} onChange={() => handleUserCheckboxChange(userId)} onClick={(e) => e.stopPropagation()} className="forward-checkbox" />
                </div>
              )
            })
          )}
        </div>
      </>
    )
  }

  const renderCommunitiesTab = () => {
    const eligibleIds = filteredCommunitiesForForward
      .filter((c) => !c.isCreateOwn && !(c.admin_only_message && !isAdminUser))
      .map((c) => c.id)
      .filter(Boolean)
    const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selectedCommunitiesForForward.includes(id))

    return (
      <>
        <div className="search-section">
          <InputText placeholder="Search communities" value={forwardSearchTerm} onChange={(e) => setForwardSearchTerm(e?.target?.value)} className="input-field" />
        </div>

        <div className="selected-info">
          <div className="selected-count">
            {t('SELECTED')} {selectedCommunitiesForForward?.length} communit{selectedCommunitiesForForward?.length !== 1 ? 'ies' : 'y'}
          </div>
          <div className="select-all-container">
            {t('SELECT_ALL')} <input type="checkbox" checked={allSelected} onChange={handleSelectAllCommunities} className="select-all-checkbox" disabled={filteredCommunitiesForForward?.length === 0} />
          </div>
        </div>

        <div className="forward-list">
          {filteredCommunitiesForForward?.length === 0 ? (
            <div className="no-results">{t('NO_COMMUNITIES_FOUND')}</div>
          ) : (
            filteredCommunitiesForForward?.map((community) => {
              const disabled = community?.admin_only_message && !isAdminUser

              return (
                <div key={community?.id} className={`forward-list-item ${selectedCommunitiesForForward?.includes(community?.id) ? 'selected' : ''} ${disabled ? 'disabled' : ''}`} onClick={() => handleCommunityCheckboxChange(community?.id)}>
                  <div className="community-info">
                    {community?.community_url ? <img src={community?.community_url} alt={community?.name} className="community-avatar" /> : <div className="community-placeholder">{community?.name?.[0] || 'C'}</div>}
                    <div className="community-name">
                      <div>{community?.name}</div>
                      {disabled && <span className="admin-only-tag">Admin only messages</span>}
                    </div>
                  </div>
                  <input type="checkbox" checked={selectedCommunitiesForForward?.includes(community?.id)} onChange={() => handleCommunityCheckboxChange(community?.id)} onClick={(e) => e.stopPropagation()} className="forward-checkbox" disabled={disabled} />
                </div>
              )
            })
          )}
        </div>
      </>
    )
  }

  const renderForwardModalBody = () => {
    const totalSelected = activeTab === 'users' ? selectedUsersForForward.length : selectedCommunitiesForForward.length

    return (
      <div className="forward-message">
        <div className="forward-tabs">
          <button
            type="button"
            className={`forward-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('users')
              setForwardSearchTerm('')
            }}
          >
            {t('PERSONAL_CHATS')}
          </button>
          <button
            type="button"
            className={`forward-tab ${activeTab === 'communities' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('communities')
              setForwardSearchTerm('')
            }}
          >
            {t('COFFEE_COMMUNITIES')}
          </button>
        </div>

        {activeTab === 'users' ? renderUsersTab() : renderCommunitiesTab()}
      </div>
    )
  }

  const renderForwardModalFooter = () => {
    const totalSelected = activeTab === 'users' ? selectedUsersForForward.length : selectedCommunitiesForForward.length

    return (
      <div className="forward-message-actions">
        <Button buttonType="CLOSE" onButtonClick={handleCloseForwardModal} />
        <Button buttonType="CUSTOM" buttonLabel={t('FORWARD')} disabled={isForwarding || totalSelected === 0} onButtonClick={handleForward} />
      </div>
    )
  }

  return <DialogUIModal title={t('FORWARD_MESSAGE')} showModal={forwardModalVisible} handleClose={handleCloseForwardModal} bodyContent={renderForwardModalBody} footerContent={renderForwardModalFooter} customClassName="forward-message-dialog-container" />
}

export default ForwardMessageModal
